import { GetServerSidePropsContext } from 'next'
import Image from 'next/image'
import type { SubmitEventHandler } from 'react'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { getI18nProps } from '../../lib/getStatic'
import routes from '../../config/route'
import { resolveLocale } from '../../lib/resolveLocale'
import useLocale from '../../hooks/useLocale'
import Link from '../../components/shared/Link'
import { BlogPost } from '../../lib/types/Contentful/BlogPost'
import { BlogTag } from '../../lib/types/Contentful/BlogTag'
import { contentfulClient } from '../../lib/Contentful/client'
import { BreadcrumbJsonLd, NextSeo } from 'next-seo'
import { HeroIcons } from '../../lib/utils/heroIcons'
import gtmService from '../../services/gtm.service'

const BLOG_POSTS_PER_PAGE = 7

interface BlogPageProps {
  posts: BlogPost[]
  tags: BlogTag[]
  filters: {
    search: string
    type: string
  }
  pagination: {
    page: number
    totalPages: number
    total: number
  }
}

const getSingleQueryValue = (value: string | string[] | undefined) =>
  Array.isArray(value) ? value[0] || '' : value || ''

const getCleanPage = (value: string | string[] | undefined) => {
  const page = Number(getSingleQueryValue(value))

  return Number.isFinite(page) && page > 0 ? Math.floor(page) : 1
}

const getBlogPath = (locale: string) =>
  locale === (process.env.NEXT_PUBLIC_DEFAULT_LOCALE || 'fr')
    ? routes.blog
    : `/${locale}${routes.blog}`

export default function BlogPage({
  posts,
  tags,
  filters,
  pagination,
}: BlogPageProps) {
  const { t } = useTranslation('blog')
  const { locale } = useLocale()
  const router = useRouter()
  const blogPath = getBlogPath(locale)
  const baseUrl = process.env.NEXT_PUBLIC_BASEURL || ''
  const [isListLoading, setIsListLoading] = useState(false)

  useEffect(() => {
    const blogBase = blogPath.replace(/\/$/, '')

    const handleStart = (url: string) => {
      // Only show the loading state for list navigations (pagination / type
      // filters), not when navigating to an individual blog post.
      const targetPath = url.split('?')[0].replace(/\/$/, '')
      if (targetPath === blogBase) {
        setIsListLoading(true)
      }
    }
    const handleDone = () => setIsListLoading(false)

    router.events.on('routeChangeStart', handleStart)
    router.events.on('routeChangeComplete', handleDone)
    router.events.on('routeChangeError', handleDone)

    return () => {
      router.events.off('routeChangeStart', handleStart)
      router.events.off('routeChangeComplete', handleDone)
      router.events.off('routeChangeError', handleDone)
    }
  }, [router.events, blogPath])

  const activeSearch = filters.search
  const activeType = filters.type
  const renderablePosts = (posts ?? []).filter(
    (post) =>
      post?.fields?.slug &&
      post?.fields?.title &&
      post?.fields?.tag?.fields?.name,
  )
  const [featuredPost, ...standardPosts] = renderablePosts

  const formatDate = (date: string) =>
    new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(date))

  const getCoverImage = (post: BlogPost) => {
    const imageUrl = post.fields.coverImage?.fields?.file?.url

    return imageUrl
      ? `https:${imageUrl}`
      : '/img/ornaments/horizon-building.png'
  }

  const getCoverAlt = (post: BlogPost) =>
    post.fields.coverImage?.fields?.title || post.fields.title

  const buildBlogHref = ({
    page = 1,
    search = activeSearch,
    type = activeType,
  }: {
    page?: number
    search?: string
    type?: string
  }) => {
    const params = new URLSearchParams()
    const trimmedSearch = search.trim()

    if (trimmedSearch) {
      params.set('q', trimmedSearch)
    }

    if (type) {
      params.set('type', type)
    }

    if (page > 1) {
      params.set('page', String(page))
    }

    const queryString = params.toString()

    return queryString ? `${blogPath}?${queryString}` : blogPath
  }

  const handleSearchSubmit: SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()
    const formData = new FormData(event.currentTarget)
    const search = String(formData.get('q') ?? '')
    const trimmedSearch = search.trim()

    if (trimmedSearch) {
      gtmService.contentEngagement({
        contentType: 'blog',
        contentTitle: trimmedSearch,
        interactionType: 'search',
      })
    }

    router.push(buildBlogHref({ search, page: 1 }))
  }

  const pageNumbers = Array.from(
    new Set(
      [
        1,
        pagination.page - 1,
        pagination.page,
        pagination.page + 1,
        pagination.totalPages,
      ].filter((page) => page >= 1 && page <= pagination.totalPages),
    ),
  ).sort((a, b) => a - b)

  return (
    <>
      <NextSeo
        title={t('seo.title') || ''}
        description={t('seo.description') || ''}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: 'Shelters',
            item: baseUrl || '/',
          },
          {
            position: 2,
            name: t('title'),
            item: `${baseUrl}${blogPath}`,
          },
        ]}
      />
      <div className="bg-gradient-box relative min-h-screen overflow-hidden px-3 pb-20 pt-28 text-[#13211a] tablet:px-16 desktop:px-24 2xl:px-44">
        <div className="relative mx-auto w-full">
          <div className="mb-6 max-w-3xl px-3 sm:mb-8 tablet:px-0">
            <h1 className="font-semibold text-white text-shelters-section-title">
              {t('title')}
            </h1>
            <p className="mt-3 max-w-2xl text-white/75 text-shelters-section-body">
              {t('subtitle')}
            </p>
          </div>
          <section className="overflow-hidden rounded-card border border-white/35 bg-[linear-gradient(145deg,rgba(255,255,255,0.22)_0%,rgba(255,255,255,0.08)_100%)] p-4 shadow-[0_10px_28px_rgba(18,15,62,0.22)] backdrop-blur-xl backdrop-saturate-150 sm:p-5">
            <div>
              <form
                onSubmit={handleSearchSubmit}
                className="flex w-full items-end gap-2 lg:max-w-2xl"
              >
                <label className="flex min-w-0 flex-1 flex-col gap-2">
                  <input
                    type="search"
                    name="q"
                    defaultValue={activeSearch}
                    placeholder={t('filters.searchPlaceholder') || ''}
                    className="h-12 rounded-button border border-white/30 bg-white/10 px-5 text-sm text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.16)] outline-none backdrop-blur-md transition placeholder:text-white/50 focus:border-white/60 focus:bg-white/15 focus:ring-4 focus:ring-white/10"
                  />
                </label>
                <button
                  type="submit"
                  className="h-12 shrink-0 rounded-button border border-white/60 bg-white/90 px-4 text-xs font-semibold text-[#293254] shadow-[0_10px_24px_rgba(18,15,62,0.18)] transition hover:-translate-y-0.5 hover:bg-white sm:px-5 sm:text-sm"
                >
                  {t('filters.searchCta')}
                </button>
              </form>
            </div>

            {tags.length > 0 && (
              <div className="-mx-4 mt-4 flex gap-2 overflow-x-auto px-4 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] sm:mx-0 sm:mt-5 sm:flex-wrap sm:px-0 sm:pb-0 [&::-webkit-scrollbar]:hidden">
                <Link
                  href={buildBlogHref({ type: '', page: 1 })}
                  skipLocaleHandling
                  onClick={() =>
                    gtmService.contentEngagement({
                      contentType: 'blog',
                      interactionType: 'filter_all',
                    })
                  }
                  className={`shrink-0 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold transition ${
                    !activeType
                      ? 'border-white/60 bg-white/90 text-[#293254] shadow-[0_10px_24px_rgba(18,15,62,0.18)]'
                      : 'border-white/25 bg-white/10 text-white/75 backdrop-blur-md hover:border-white/45 hover:bg-white/20 hover:text-white'
                  }`}
                >
                  {t('filters.allTypes')}
                </Link>
                {tags.map((tag) => (
                  <Link
                    key={tag.sys.id}
                    href={buildBlogHref({ type: tag.sys.id, page: 1 })}
                    skipLocaleHandling
                    onClick={() =>
                      gtmService.contentEngagement({
                        contentType: 'blog',
                        contentId: tag.sys.id,
                        contentTitle: tag.fields.name,
                        interactionType: 'filter_tag',
                      })
                    }
                    className={`shrink-0 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold transition ${
                      activeType === tag.sys.id
                        ? 'border-white/60 bg-white/90 text-[#293254] shadow-[0_10px_24px_rgba(18,15,62,0.18)]'
                        : 'border-white/25 bg-white/10 text-white/75 backdrop-blur-md hover:border-white/45 hover:bg-white/20 hover:text-white'
                    }`}
                  >
                    {tag.fields.name}
                  </Link>
                ))}
              </div>
            )}
          </section>

          <div className="relative">
            <div
              aria-busy={isListLoading}
              className={`transition-opacity duration-300 ${
                isListLoading ? 'pointer-events-none opacity-40' : ''
              }`}
            >
              {featuredPost && (
                <div className="mt-6 grid gap-4 tablet:grid-cols-2 desktop:grid-cols-4">
                  <Link
                    href={`${routes.blog}${featuredPost.fields.slug}`}
                    locale={locale}
                    onClick={() =>
                      gtmService.contentEngagement({
                        contentType: 'blog_post',
                        contentId: featuredPost.sys.id,
                        contentTitle: featuredPost.fields.title,
                        interactionType: 'post_click_featured',
                      })
                    }
                    className="group grid overflow-hidden rounded-card border border-white/35 bg-[linear-gradient(145deg,rgba(255,255,255,0.22)_0%,rgba(255,255,255,0.08)_100%)] shadow-[0_10px_28px_rgba(18,15,62,0.22)] backdrop-blur-xl backdrop-saturate-150 transition duration-300 hover:-translate-y-1 hover:border-white/50 hover:bg-[linear-gradient(145deg,rgba(255,255,255,0.28)_0%,rgba(255,255,255,0.12)_100%)] tablet:col-span-2 tablet:grid-cols-[1.1fr_0.9fr]"
                  >
                    <div className="order-2 flex flex-col justify-between p-4 tablet:order-1 tablet:p-5">
                      <div>
                        <div className="mb-3 flex flex-wrap items-center gap-2 text-[0.7rem] font-medium text-white/65">
                          <span className="rounded-full border border-white/20 bg-white/10 px-2.5 py-1 text-white/85 backdrop-blur-md">
                            {featuredPost.fields.tag.fields.name}
                          </span>
                          <span className="flex items-center gap-1">
                            <HeroIcons.CalendarIcon className="h-3.5 w-3.5" />
                            {formatDate(featuredPost.fields.publishedDate)}
                          </span>
                          <span className="flex items-center gap-1">
                            <HeroIcons.ClockIcon className="h-3.5 w-3.5" />
                            {featuredPost.fields.readTime} {t('post.minutes')}
                          </span>
                        </div>
                        <h2 className="line-clamp-2 text-xl font-semibold leading-tight tracking-[-0.035em] text-white transition group-hover:text-white/80 tablet:text-2xl">
                          {featuredPost.fields.title}
                        </h2>
                        <p className="mt-3 line-clamp-2 text-sm leading-5 text-white/70">
                          {featuredPost.fields.excerpt}
                        </p>
                      </div>

                      <HeroIcons.ArrowRightIcon className="mt-4 h-4 w-4 text-white/80 transition group-hover:translate-x-1 group-hover:text-white" />
                    </div>

                    <div className="relative order-1 h-44 overflow-hidden tablet:order-2 tablet:h-full tablet:min-h-[250px]">
                      <Image
                        src={getCoverImage(featuredPost)}
                        alt={getCoverAlt(featuredPost)}
                        fill
                        sizes="(min-width: 768px) 45vw, 100vw"
                        className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                      />
                    </div>
                  </Link>

                  {standardPosts.map((post) => (
                    <Link
                      key={post.sys.id}
                      href={`${routes.blog}${post.fields.slug}`}
                      locale={locale}
                      onClick={() =>
                        gtmService.contentEngagement({
                          contentType: 'blog_post',
                          contentId: post.sys.id,
                          contentTitle: post.fields.title,
                          interactionType: 'post_click',
                        })
                      }
                      className="group flex min-h-full flex-col overflow-hidden rounded-card border border-white/35 bg-[linear-gradient(145deg,rgba(255,255,255,0.22)_0%,rgba(255,255,255,0.08)_100%)] shadow-[0_10px_28px_rgba(18,15,62,0.22)] backdrop-blur-xl backdrop-saturate-150 transition duration-300 hover:-translate-y-1 hover:border-white/50 hover:bg-[linear-gradient(145deg,rgba(255,255,255,0.28)_0%,rgba(255,255,255,0.12)_100%)]"
                    >
                      <div className="relative h-40 overflow-hidden">
                        <Image
                          src={getCoverImage(post)}
                          alt={getCoverAlt(post)}
                          fill
                          sizes="(min-width: 1024px) 25vw, (min-width: 768px) 50vw, 100vw"
                          className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                        />
                      </div>
                      <div className="flex flex-1 flex-col p-4">
                        <div className="mb-3 flex flex-wrap items-center gap-2 text-[0.7rem] font-medium text-white/65">
                          <span className="rounded-full border border-white/20 bg-white/10 px-2.5 py-1 text-white/85 backdrop-blur-md">
                            {post.fields.tag.fields.name}
                          </span>
                          <span>
                            {post.fields.readTime} {t('post.minutes')}
                          </span>
                        </div>
                        <h2 className="line-clamp-2 text-base font-semibold leading-snug tracking-[-0.02em] text-white transition group-hover:text-white/80">
                          {post.fields.title}
                        </h2>
                        <p className="mt-2 line-clamp-2 text-xs leading-5 text-white/70">
                          {post.fields.excerpt}
                        </p>
                        <div className="mt-auto flex items-center justify-between pt-4 text-[0.7rem] font-medium text-white/65">
                          <span className="flex items-center gap-1">
                            <HeroIcons.CalendarIcon className="h-3.5 w-3.5" />
                            {formatDate(post.fields.publishedDate)}
                          </span>
                          <HeroIcons.ArrowRightIcon className="h-4 w-4 transition group-hover:translate-x-1 group-hover:text-white" />
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {!featuredPost && (
                <div className="mt-8 rounded-container border border-white/35 bg-[linear-gradient(145deg,rgba(255,255,255,0.22)_0%,rgba(255,255,255,0.08)_100%)] p-8 text-center shadow-[0_10px_28px_rgba(18,15,62,0.22)] backdrop-blur-xl backdrop-saturate-150 sm:p-12">
                  <p className="text-xl font-semibold tracking-[-0.03em] text-white">
                    {t('filters.emptyTitle')}
                  </p>
                  <p className="mx-auto mt-3 max-w-lg text-sm leading-6 text-white/70">
                    {t('filters.emptyText')}
                  </p>
                  <Link
                    href={blogPath}
                    skipLocaleHandling
                    className="mt-6 inline-flex rounded-button border border-white/60 bg-white/90 px-5 py-3 text-sm font-semibold text-[#293254] transition hover:bg-white"
                  >
                    {t('filters.allTypes')}
                  </Link>
                </div>
              )}

              {pagination.totalPages > 1 && (
                <nav
                  aria-label="Blog pagination"
                  className="mt-8 flex flex-col gap-3 rounded-container border border-white/35 bg-[linear-gradient(145deg,rgba(255,255,255,0.22)_0%,rgba(255,255,255,0.08)_100%)] p-4 shadow-[0_10px_28px_rgba(18,15,62,0.22)] backdrop-blur-xl backdrop-saturate-150 sm:flex-row sm:items-center sm:justify-between"
                >
                  <p className="text-sm font-medium text-white/75">
                    {t('pagination.page', {
                      page: pagination.page,
                      totalPages: pagination.totalPages,
                    })}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      href={buildBlogHref({
                        page: Math.max(1, pagination.page - 1),
                      })}
                      skipLocaleHandling
                      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
                        pagination.page === 1
                          ? 'pointer-events-none border border-white/10 bg-white/5 text-white/35'
                          : 'border border-white/25 bg-white/10 text-white/80 backdrop-blur-md hover:bg-white/90 hover:text-[#293254]'
                      }`}
                    >
                      <HeroIcons.ArrowLeftIcon className="h-4 w-4" />
                      {t('pagination.previous')}
                    </Link>
                    {pageNumbers.map((page, index) => (
                      <span key={page} className="flex items-center gap-2">
                        {index > 0 && page - pageNumbers[index - 1] > 1 && (
                          <span className="px-1 text-sm font-semibold text-white/55">
                            ...
                          </span>
                        )}
                        <Link
                          href={buildBlogHref({ page })}
                          skipLocaleHandling
                          className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold transition ${
                            pagination.page === page
                              ? 'border border-white/60 bg-white/90 text-[#293254] shadow-[0_10px_24px_rgba(18,15,62,0.18)]'
                              : 'border border-white/25 bg-white/10 text-white/80 backdrop-blur-md hover:bg-white/25 hover:text-white'
                          }`}
                        >
                          {page}
                        </Link>
                      </span>
                    ))}
                    <Link
                      href={buildBlogHref({
                        page: Math.min(
                          pagination.totalPages,
                          pagination.page + 1,
                        ),
                      })}
                      skipLocaleHandling
                      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
                        pagination.page === pagination.totalPages
                          ? 'pointer-events-none border border-white/10 bg-white/5 text-white/35'
                          : 'border border-white/25 bg-white/10 text-white/80 backdrop-blur-md hover:bg-white/90 hover:text-[#293254]'
                      }`}
                    >
                      {t('pagination.next')}
                      <HeroIcons.ArrowRightIcon className="h-4 w-4" />
                    </Link>
                  </div>
                </nav>
              )}
            </div>

            {isListLoading && (
              <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center">
                <span className="flex items-center gap-3 rounded-full border border-white/30 bg-[#1b2240]/80 px-5 py-3 text-sm font-semibold text-white shadow-[0_10px_28px_rgba(18,15,62,0.3)] backdrop-blur-xl">
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  {t('filters.loading')}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

// 👇 Server-Side Rendering
export async function getServerSideProps(context: GetServerSidePropsContext) {
  const locale = resolveLocale(context)
  const blogPath = getBlogPath(locale)
  const search = getSingleQueryValue(context.query.q).trim()
  const page = getCleanPage(context.query.page)

  const tagsEntries = await contentfulClient.getEntries({
    content_type: 'blogTag',
    order: ['fields.name'],
    locale,
  })
  const tags = tagsEntries.items.filter(
    (tag: any) => tag?.sys?.id && tag?.fields?.name,
  )
  const requestedType = getSingleQueryValue(context.query.type)
  const type = tags.some((tag: any) => tag.sys.id === requestedType)
    ? requestedType
    : ''
  const entriesQuery: Record<string, any> = {
    content_type: 'blogPost',
    order: ['-fields.publishedDate'],
    locale,
    include: 3,
    limit: BLOG_POSTS_PER_PAGE,
    skip: (page - 1) * BLOG_POSTS_PER_PAGE,
  }

  if (search) {
    entriesQuery.query = search
  }

  if (type) {
    entriesQuery['fields.tag.sys.id'] = type
  }

  const entries = await contentfulClient.getEntries(entriesQuery)
  const totalPages = Math.max(1, Math.ceil(entries.total / BLOG_POSTS_PER_PAGE))

  if (page > totalPages) {
    const query = new URLSearchParams()

    if (search) {
      query.set('q', search)
    }

    if (type) {
      query.set('type', type)
    }

    if (totalPages > 1) {
      query.set('page', String(totalPages))
    }

    return {
      redirect: {
        destination: query.toString()
          ? `${blogPath}?${query.toString()}`
          : blogPath,
        permanent: false,
      },
    }
  }

  const i18nProps = await getI18nProps(context, [
    'common',
    'index',
    'blog',
    'navbar',
    'footer',
    'modals',
    'newsletter',
  ])

  if (process.env.TARGET_ENV === 'production') {
    context.res.setHeader(
      'Vercel-CDN-Cache-Control',
      'public, s-maxage=21600, stale-while-revalidate=86400',
    )
  }

  return {
    props: {
      ...i18nProps,
      posts: entries.items,
      tags,
      filters: {
        search,
        type,
      },
      pagination: {
        page,
        totalPages,
        total: entries.total,
      },
    },
  }
}
